/**
 * The OPEN-REDIRECT boundary for every post-auth destination: `redirect=` is attacker-controllable (it rides in
 * a link, and on the server in the forgeable OAuth `state`) and `resolvePostLoginPath` hands it straight to
 * `navigate({ href })` / `c.redirect()`, so only a SAME-ORIGIN relative path is a destination. Tested against
 * the percent-DECODED form, so `/%2Fevil.example` cannot smuggle a protocol-relative URL past it, while the
 * ORIGINAL string returns byte-identical. `sanitizeReturnPath` guards the raw OAuth state; this closes the
 * NESTED value it cannot see, `/login?redirect=<absolute>` being itself a valid relative path.
 */
export function safeRelativePath(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === '') return undefined
  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return undefined
  }
  // A single leading slash. This alone rejects every scheme (`javascript:`, `data:`, `https://…`) since
  // none of them can start with `/`; the explicit pair below rejects protocol-relative `//host` and the
  // `/\host` form some browsers normalize to it.
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/\\')) return undefined
  // No control chars (incl. CR/LF) that could split a Location header or smuggle a second URL. A
  // code-point scan, not a control-char regex (which the linter forbids).
  for (const ch of decoded) {
    const code = ch.charCodeAt(0)
    if (code <= 0x1f || code === 0x7f) return undefined
  }
  return raw
}
